import { toPng } from 'html-to-image';
import { serializeError } from 'serialize-error';
import * as Sentry from '@sentry/react';
import { EventNames, track } from 'utils/analytics.utils';
import { getPosthog } from 'utils/posthog-loader';
import { enableManualSentryErrorSending, isProd } from 'constants/config';
import { apiClient } from 'services/api-client';
import { getRequestPath, opaqueSecretSegment } from 'services/api-client/client';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import isError from 'lodash/isError';
import isArray from 'lodash/isArray';
import isPlainObject from 'lodash/isPlainObject';
import mapValues from 'lodash/mapValues';
import { AxiosError } from 'axios';

export const consoleCaptureIntegration = () => Sentry.captureConsoleIntegration({ levels: ['error'] });

export const ignoreErrors = ["Failed to read the 'cssRules' property from 'CSSStyleSheet'", /\(api\.segment\.io\)$/];

const EXTRA_DATA_CONTEXT_NAME = 'Env0 Extra Data Section';
// When setting Sentry context values explicitly, undefineds are ignored
// So use this string instead to represent undefined values
const SENTRY_CONTEXT_UNDEFINED_VALUE = '[undefined]';

// default image if the existing cannot be loaded
// https://github.com/bubkoo/html-to-image/issues/314#issuecomment-1274574340
const BROKEN_IMAGE_PLACEHOLDER =
  'data:image/gif;base64,R0lGODlhGQAZAPQAAISChKWipcbT7+fr/8bf51KuOdbf93vDc87b9/f3997n/2u2Y9bf/5SSlJzHtcbT9////+/z/1KyOdbj94zHjNbb9/f3/2u6Y9bj/5yenLXP1sbX9wAAAAAAAAAAAAAAACH/C1hNUCBEYXRhWE1QPD94cGFja2V0IGJlZ2luPSLvu78iIGlkPSJXNU0wTXBDZWhpSHpyZVN6TlRjemtjOWQiPz4gPHg6eG1wbWV0YSB4bWxuczp4PSJhZG9iZTpuczptZXRhLyIgeDp4bXB0az0iWE1QIENvcmUgNS41LjAiPiA8cmRmOlJERiB4bWxuczpyZGY9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkvMDIvMjItcmRmLXN5bnRheC1ucyMiPiA8cmRmOkRlc2NyaXB0aW9uIHJkZjphYm91dD0iIi8+IDwvcmRmOlJERj4gPC94OnhtcG1ldGE+ICAgPD94cGFja2V0IGVuZD0idyI/PgH//v38+/r5+Pf29fTz8vHw7+7t7Ovq6ejn5uXk4+Lh4N/e3dzb2tnY19bV1NPS0dDPzs3My8rJyMfGxcTDwsHAv769vLu6ubi3trW0s7KxsK+urayrqqmop6alpKOioaCfnp2cm5qZmJeWlZSTkpGQj46NjIuKiYiHhoWEg4KBgH9+fXx7enl4d3Z1dHNycXBvbm1sa2ppaGdmZWRjYmFgX15dXFtaWVhXVlVUU1JRUE9OTUxLSklIR0ZFRENCQUA/Pj08Ozo5ODc2NTQzMjEwLy4tLCsqKSgnJiUkIyIhIB8eHRwbGhkYFxYVFBMSERAPDg0MCwoJCAcGBQQDAgEAACwAAAAAGQAZAAAF/2DgWGRpmlkTaIkFvWIkzzSdZY42JS8UBIqJcEicKBSZAGSSIfB8AaHBMBlYB5OpMAlJaAhOyy+LKLsgljKCyn1pMruxATGI9CARxZqZ0fgDKg5yCHZ3EAN7KQ2LjA1JVWeGiGVCV1aAjwOGPZN0dTNijlFzaqWlWlQKA5hRCBuvsLGnWgqsEwgCsLmxurAIE7a4rwLEucW7u78EGmO4x8/QAr8uj87PfhrRCmfV0djRmi/dxA4OC+foF34CE4bjAuUFBRL05zkICj0JCd0a5vQAAx4wZEuAPwoLAgYs18NCBFsEHBxQqPDJCyyAfiw7kJCiBAcE9H0BpiJARI8AQyL2IKCOZIAMFCZ6dLDvRYKYEig4SPHywAWUGu4kAHiAwqIQADs=';

export async function getScreenshot() {
  try {
    const screenshotBase64Uri = await toPng(document.body, {
      cacheBust: true,
      imagePlaceholder: BROKEN_IMAGE_PLACEHOLDER
    });
    const { s3Uri } = await apiClient.shared.uploadScreenshot(screenshotBase64Uri);
    const downloadCommand = `aws s3 cp "${s3Uri}" ./my-error.png`;
    return downloadCommand;
  } catch (err) {
    // ignore
    console.warn('could not upload error image', err);
  }
}

const getUserSnapshotContext = async () => {
  const screenshot = (await getScreenshot()) ?? SENTRY_CONTEXT_UNDEFINED_VALUE;
  const posthogDistinctId = getPosthog()?.get_distinct_id?.() ?? SENTRY_CONTEXT_UNDEFINED_VALUE;

  return { posthogDistinctId, screenshot };
};

export const reportError = async (errorSource: string, originalError: any, extraInfo?: Record<string, unknown>) => {
  try {
    let errorToReport = originalError;
    if (!isError(originalError)) {
      // error is probably string, so wrap it:
      if (isString(originalError)) {
        errorToReport = new Error(`String error reported: ${originalError}`, { cause: originalError });
      } else {
        // error is object
        errorToReport = new Error('Wrapped object as error, look on inner cause', { cause: originalError });
      }
    }

    const justMetadata: Record<string, unknown> = {
      ...extraInfo,
      env0ErrorSource: errorSource
    };

    Sentry.withScope(scope => {
      scope.setTag('ENV0_ERROR_SOURCE', errorSource);

      const context: Record<string, unknown> = { ...justMetadata, serializedError: serializeError(errorToReport) };
      if (!!errorToReport.cause) {
        // doing so in different field, to manually support "cause" spec, because both `serializeError` and Sentry doesn't support it.
        // Note that `serializeError` lib do support it on v11 (we need to upgrade), but Sentry doesn't support it yet
        // spec: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/cause
        context.errorCause = serializeError(errorToReport.cause);
      }
      scope.setContext(EXTRA_DATA_CONTEXT_NAME, context);

      Sentry.captureException(errorToReport);
    });

    track(EventNames.ERROR_REPORTED, justMetadata);
  } catch (err) {
    console.error('failed to alert sentry', err);
  }
};

export const addUserSnapshotContextToEvent = async (event: Sentry.Event) => {
  const userSnapshotContext = await getUserSnapshotContext();
  if (isEmpty(event.contexts)) {
    event.contexts = { [EXTRA_DATA_CONTEXT_NAME]: userSnapshotContext };
  } else {
    event.contexts[EXTRA_DATA_CONTEXT_NAME] = { ...event.contexts[EXTRA_DATA_CONTEXT_NAME], ...userSnapshotContext };
  }
};

if (enableManualSentryErrorSending && !isProd) {
  (window as any).ourSentry = { reportError };
}

// make sure that Urls with ids will look the same, as they shouldn't create different issues
const replaceIds = (url: string) =>
  url
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/g, 'UUID')
    // Auth0 user ids such as google-oauth2|1234567890, raw or url-encoded
    .replace(/[^/?#&=]+(?:\||%7C)[^/?#&=]+/gi, 'USER_ID');

// one value per endpoint, for a fingerprint or a tag: the path only, with ids and invitation tokens masked
const getRequestPathPattern = (url?: string) => {
  if (!url) {
    return '';
  }

  try {
    // no leading slash, the form fingerprints already have, so existing Sentry issues keep their events
    return replaceIds(getRequestPath({ url })).replace(/^\//, '');
  } catch {
    // new URL throws on inputs like '//', and a throw in beforeSend drops the whole event
    return '';
  }
};

// httpClientIntegration marks its events auto.http.client.fetch or auto.http.client.xhr
const isHttpClientEvent = (event: Sentry.Event) =>
  event.exception?.values?.[0]?.mechanism?.type?.startsWith('auto.http.client.') ?? false;

export const updateFingerprint = (event: Sentry.Event, hint: Sentry.EventHint) => {
  const exception = hint.originalException;

  if (exception instanceof AxiosError) {
    event.fingerprint = createFingerprint(
      exception.response?.status ?? exception.response?.statusText ?? '',
      exception.message ?? '',
      getRequestPathPattern(exception.config?.url),
      exception.response?.data ?? ''
    );
  } else if (isHttpClientEvent(event)) {
    event.fingerprint = createFingerprint(
      event.contexts?.response?.status_code ?? '',
      event.message ?? '',
      getRequestPathPattern(event.request?.url)
    );
  }
};

export const addRequestUrlCustomTag = (event: Sentry.Event, hint: Sentry.EventHint) => {
  const originalException = hint.originalException;
  let url: string | undefined;

  if (originalException instanceof AxiosError && originalException?.config?.url) {
    url = originalException.config.url;
  } else if (isHttpClientEvent(event)) {
    url = event.request?.url;
  }

  const requestPath = getRequestPathPattern(url);
  // beforeSend runs after the scope is applied to the event, so a tag set on a scope here never reaches it
  if (requestPath) {
    event.tags = { ...event.tags, 'request.url': requestPath };
  }
};

const createFingerprint = (status: string | number, message: string, urlPattern: string, responseData?: string) => {
  return ['{{ default }}', JSON.stringify(status), message, urlPattern, JSON.stringify(responseData)].filter(Boolean);
};

const FILTERED = '[Filtered]';
// isSecretKey from @env0/common-logger, plus the Authorization header; the browser cannot import that module, as it
// pulls in winston. A match anywhere in the key counts, except on ids and descriptors such as tokenId or nextToken
const secretKey =
  /authorization|password|passphrase|secret|token|api[-_]?key|private[-_]?key|credential|service[-_]?account[-_]?key|kube[-_]?config/i;
const isSecretKey = (key: string) => secretKey.test(key) && !/(Ids?|Type|Name|Format|Table)$|^nextToken$/.test(key);
// 32 or more hex characters after a slash, wherever the url sits in the string. A bare id such as a trace id has no
// slash, so it stays
const opaqueSecretInPath = /\/[0-9a-f]{32,}(?![0-9a-z])/gi;
// a key=value pair after ? # or &, or opening a bare query string such as the SDK's url.query attribute. Whitespace
// and quotes end it too, so in a log line one pair cannot swallow the urls that follow it
const urlParam = /(^|[?#&])([^=&#/?\s"'<>]*)=([^&#\s"'<>]*)/g;
// a credential or a "key":"value" pair that a log line wrote into text, as when it serialises request headers. The
// credential needs a digit and 16 characters, so prose such as "bearer authentication" stays
const bearerCredential = /\b(Bearer\s+)(?=[\w\-.~+/]*\d)[\w\-.~+/]{16,}=*/gi;
const jsonPair = /"([^"\\]*)"(\s*:\s*)"((?:[^"\\]|\\.)*)"/g;

// runs on every string: the SDK files urls under keys that change between versions, and logs embed urls in text.
// Invite tokens travel in the path and the query, and Auth0's implicit flow returns its tokens in the fragment
const scrubText = (value: string): string =>
  value
    .replace(opaqueSecretInPath, `/${FILTERED}`)
    .replace(urlParam, (param, separator, key, paramValue) =>
      isSecretKey(key) || opaqueSecretSegment.test(paramValue) ? `${separator}${key}=${FILTERED}` : param
    )
    .replace(bearerCredential, `$1${FILTERED}`)
    .replace(jsonPair, (pair, key, colon) => (isSecretKey(key) ? `"${key}"${colon}"${FILTERED}"` : pair));

const scrubData = <T>(value: T, key = ''): T => {
  if (isSecretKey(key)) return FILTERED as T;
  if (isString(value)) return scrubText(value) as T;
  if (isArray(value)) return value.map(item => scrubData(item)) as T;
  if (isPlainObject(value)) {
    const scrubbed = mapValues(value as Record<string, unknown>, (item, itemKey) => scrubData(item, itemKey));
    // an axios config's request body: forms post secrets under plain keys, such as the VCS token form's `value`
    return (key === 'config' && 'data' in scrubbed ? { ...scrubbed, data: FILTERED } : scrubbed) as T;
  }

  return value;
};

// for beforeSend and beforeSendTransaction: by then the SDK has normalised the event, so every value is plain data
export const scrubEvent = <T extends Sentry.Event>(event: T): T => ({
  ...event,
  message: event.message && scrubText(event.message),
  transaction: event.transaction && scrubText(event.transaction),
  exception: event.exception && {
    ...event.exception,
    values: event.exception.values?.map(exception => ({
      ...exception,
      value: exception.value && scrubText(exception.value)
    }))
  },
  tags: scrubData(event.tags),
  request: scrubData(event.request),
  extra: scrubData(event.extra),
  contexts: scrubData(event.contexts),
  breadcrumbs: event.breadcrumbs?.map(breadcrumb => ({
    ...breadcrumb,
    message: breadcrumb.message && scrubText(breadcrumb.message),
    data: scrubData(breadcrumb.data)
  })),
  spans: event.spans?.map(span => ({
    ...span,
    description: span.description && scrubText(span.description),
    data: scrubData(span.data)
  })),
  fingerprint: event.fingerprint?.map(scrubText)
});
